import React, { useEffect } from "react";

import { ZodProvider } from "@autoform/zod";
import { useNotification, useParsed } from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { AutoForm } from "@/components/ui/autoform";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { useSitesRepository } from "@/pages/tina/sites/hooks/use-sites-repository";
import {
  buildSiteUpdatePayload,
  extractKnownVenueId,
} from "@/pages/tina/sites/lib/site-edit";
import {
  type SiteEditFormType,
  siteEditSchema,
} from "@/pages/tina/sites/schemas/site.edit.form";

const schemaProvider = new ZodProvider(siteEditSchema);

export const SitesEdit = () => {
  const { id } = useParsed();
  const name = id?.toString();
  const repo = useSitesRepository();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const setTitle = useDocumentTitle();

  const [values, setValues] = React.useState<SiteEditFormType>();
  const [currentConfig, setCurrentConfig] = React.useState<Record<
    string,
    unknown
  > | null>(null);

  useEffect(() => {
    if (!name) return;
    setLoading(true);
    repo
      .get(name)
      .then((site) => {
        setCurrentConfig(site.config);
        setValues({
          enabled: site.enabled,
          known_venue_id: extractKnownVenueId(site.config),
        });
        setTitle(`Edit ${site.name}`);
      })
      .catch((error) => {
        open?.({
          type: "error",
          message: "Failed to load site",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const onSubmit = async (formValues: SiteEditFormType) => {
    if (!name) return;
    const payload = buildSiteUpdatePayload(
      {
        enabled: formValues.enabled,
        known_venue_id: formValues.known_venue_id ?? null,
      },
      currentConfig,
    );
    try {
      await repo.update(name, payload);
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to update site",
        description: toHumanErrorMessage(error),
      });
    }
  };

  return (
    <div className="flex w-full max-w-2xl flex-col gap-4 p-8">
      <div>
        <h1 className="text-2xl font-semibold">
          Edit site{name ? ` · ${name}` : ""}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Update known venue and enabled state. Other config is preserved.
        </p>
      </div>
      {values && (
        <AutoForm
          defaultValues={values}
          schema={schemaProvider}
          onSubmit={onSubmit}
        >
          <BottomActionBar>
            <Button type="submit">Save</Button>
          </BottomActionBar>
        </AutoForm>
      )}
    </div>
  );
};
